import {PointerCursorModel, PointerRayModel, usePointerXRInputSourceEvents, useRayPointer, useXR, XRControllerState, XRSpace, DefaultXRControllerOptions, useXRInputSourceStateContext, XRControllerModel, DefaultXRInputSourceRayPointerOptions, CombinedPointer, DefaultXRControllerGrabPointer, DefaultXRInputSourceRayPointer, XRSpaceType, useApplyXRSpaceMatrix, useXRSpace, useXRInputSourceState} from "@react-three/xr"
import {createGetXRSpaceMatrix} from "@pmndrs/xr/internals";
import React, {forwardRef, ReactNode, Suspense, useCallback, useImperativeHandle, useRef} from "react";
import * as THREE from "three";
import {XRInputSourceTeleportPointer} from "./Teleport.js";
import {RunInAction} from "mobx-graphlink";
import {store} from "../../../Store/index.js";
import {AssertUnreachable} from "web-vcore";
import {observer_mgl_r3} from "../General.js";
import {GetControllerSide_Final} from "../../../Store/main/settings/vr.js";

function spreadable<T extends object>(value: T | boolean | null | undefined): T | object {
	if (typeof value == "object" && value != null) return value;
	return {};
}

/**
 * default controller implementation with grab and ray pointers
 *
 * properties
 * - `model` options for configuring the controller apperance
 * - `grabPointer` options for configuring the grab pointer
 * - `rayPointer` options for configuring the ray pointer
 */
export const XRController = observer_mgl_r3((props: {side: "left" | "right", options: DefaultXRControllerOptions})=>{
	const {side} = props;

	const side_forOptions = GetControllerSide_Final(side);
	const options = side_forOptions == "left"
		? {model: null, grabPointer: null, rayPointer: true, teleportPointer: true}
		: {model: null, grabPointer: null, rayPointer: true, teleportPointer: false};
	return (
	  <>
			{options.model !== false && (
				<Suspense>
					<XRControllerModel {...spreadable(options.model)}/>
				</Suspense>
			)}
			<CombinedPointer>
				<XRInputSourceSpaceRetriever/>
				{options.grabPointer != false && <DefaultXRControllerGrabPointer {...spreadable(options.grabPointer)}/>}
				{options.rayPointer != false && (
					<XRInputSourceRayPointer makeDefault minDistance={0.2} {...spreadable(options.rayPointer)}/>
				)}
				{options.teleportPointer != false && (
					//<DefaultXRInputSourceTeleportPointer {...spreadable(options.teleportPointer)} />
					<XRInputSourceTeleportPointer {...spreadable(options.teleportPointer)}/>
				)}
			</CombinedPointer>
	  </>
	)
});

export let controllerLeft_targetRaySpaceGroup: THREE.Group|n;
export let controllerRight_targetRaySpaceGroup: THREE.Group|n;
export function GetControllerRaySpaceGroup(side: "left" | "right") {
	if (side == "left") return controllerLeft_targetRaySpaceGroup;
	if (side == "right") return controllerRight_targetRaySpaceGroup;
	AssertUnreachable(side);
}

export const getControllerPosAndDir = (controller: XRControllerState|n, controllerSide?: "left" | "right"): {pos: THREE.Vector3|null, dir: THREE.Vector3|null, dirQuat: THREE.Quaternion|null}=>{
	const result = {pos: null as THREE.Vector3|null, dir: null as THREE.Vector3|null, dirQuat: null as THREE.Quaternion|null};

	//const controller = useVR ? useXRInputSourceState("controller", controllerSide) : null;
	const controllerObj = controller?.object;
	if (controller == null || controllerObj == null) return result;
	if (controllerSide == null) {
		if (["left", "right"].includes(controller?.inputSource.handedness as any)) {
			controllerSide = controller!.inputSource.handedness as "left" | "right";
		} else {
			throw new Error("Controller handedness is not defined. (either as param, or found from passed controller)");
		}
	}

	const origin = new THREE.Vector3();
	controllerObj.getWorldPosition(origin);
	result.pos = origin;

	// Extract direction (negative z-axis) from matrix
	const direction = new THREE.Vector3(0, 0, -1);
	const controller_matrixWorld = GetControllerRaySpaceGroup(controllerSide)?.matrixWorld;
	if (controller_matrixWorld == null) return result;

	const rotationMatrix = new THREE.Matrix4().fromArray(controller_matrixWorld.elements.slice());
	rotationMatrix.setPosition(0, 0, 0); // Remove translation part
	direction.applyMatrix4(rotationMatrix).normalize();
	result.dir = direction;
	result.dirQuat = Vector3RotationToQuaternion(direction);

	return result;
};
export function Vector3RotationToQuaternion(vector: THREE.Vector3) {
	const quaternion = new THREE.Quaternion();
	quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), vector);
	return quaternion;
}

export const XRInputSourceSpaceRetriever = ()=>{
	const state = useXRInputSourceStateContext();
	return (
		<XRSpace space="target-ray-space" ref={c=>{
			if (!c) return;
			RunInAction("XRInputSourceRayPointer.xrSpaceRef", ()=>{
				if (state.inputSource.handedness == "left") {
					controllerLeft_targetRaySpaceGroup = c as THREE.Group;
				} else {
					controllerRight_targetRaySpaceGroup = c as THREE.Group;
				}
			});
		}}/>
	);
};

export const XRInputSourceRayPointer = (options: DefaultXRInputSourceRayPointerOptions)=>{ //props: {state: XRControllerState})=>{
	//const {state} = props;
	const state = useXRInputSourceStateContext();
	const ref = useRef<THREE.Object3D|null>(null);
	const pointer = useRayPointer(ref, state, options);
	usePointerXRInputSourceEvents(pointer, state.inputSource, "select", state.events);
	/*const rayModelOptions = options.rayModel;
	const cursorModelOptions = options.cursorModel;*/
	return (
		<XRSpace space="target-ray-space" ref={c=>void(ref.current = c)}>
			<PointerRayModel pointer={pointer} maxLength={100} size={.001}
				//opacity={.5}
				opacity={0} // see comments for storeOpts.controller.[left/right].rayPointer in Scene.tsx
			/>
			{/*<PointerCursorModel pointer={pointer} opacity={.5}/>*/}
		</XRSpace>
	);
};